import React, { useState, useRef } from "react";
import { Button, Icon, Modal, Header, Label, Segment, Grid } from "semantic-ui-react";
import CustomInputTextComponent from "components/Form/CustomInputTextComponent";
import PatientData from "components/Others/PatientData";
import { Field, Formik, Form } from "formik";
import * as Yup from "yup";
import { checkMimeType } from "utils/file";
import { toast } from "react-toastify";
import BlueButtonEditBloc from "components/Buttons/BlueButtonEditBloc";

const schemaValidation = Yup.object().shape({
	folder: Yup.number().required("Champ requis"),
});

const CheckinModal = ({ open, close, submit, patient }) => {
	const [files, setFiles] = useState([]);
	const [emailValid, setEmailValid] = useState(false);
	const [editEmail, setEditEmail] = useState(false);
	const fileUploadRef = useRef(null);

	const onFileChange = async (e) => {
		if (e !== undefined) {
			e.preventDefault();
		}

		if (e.target.files[0] !== undefined) {
			if (checkMimeType(e)) {
				setFiles((prevFiles) => [...prevFiles, e.target.files[0]]);
			} else {
				toast.error(this.props.t("prescriptions.formatNotSupported"));
			}
		}
	};

	const handleRemoveFile = async (fileNameToRemove) => {
		setFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileNameToRemove));
	};

	const fileToBase64 = async (file) =>
		new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				// "data:application/pdf;base64,JVBERi0x..." → on retire le header
				const base64 = reader.result.split(",")[1];
				resolve(base64);
			};
			reader.onerror = reject;
		});

	const fillCheckinToSubmit = async (values) => {
		const filesData = await Promise.all(
			files.map(async (file) => ({
				name: file.name,
				data: await fileToBase64(file),
			}))
		);
		const checkin = {
			last_name: patient.last_name,
			birth_name: patient.birth_name?.length > 0 ? patient.birth_name : patient.last_name,
			first_name: patient.first_name,
			birth_date: patient.birth_date,
			mobile_phone: patient.mobile_phone,
			email: values.email || patient.email,
			matricule: patient.cns,
			identity_number: patient.identity_number,
			gender: patient.gender,
			is_at_center: false,
			mylab_id: patient.id ? patient.id.toString() : "",
			files: filesData,
		};
		return checkin;
	};

	const closeModal = () => {
		setFiles([]);
		setEmailValid(false);
		setEditEmail(false);
		close();
	};
	const submitFormToCheckin = async (values) => {
		console.log("submit form to checkin", values);
		const checkin = await fillCheckinToSubmit(values);
		submit(checkin);
		closeModal();
	};
	return (
		<Formik enableReinitialize initialValues={{ email: patient?.email || "" }} validationSchema={schemaValidation}>
			{({ submitForm, values }) => (
				<>
					<Form>
						<Modal open={open} closeOnEscape closeOnDimmerClick onClose={close}>
							<Modal.Header>Envoyer un checkin à ce patient</Modal.Header>

							<Modal.Content>
								<Grid columns={2} stackable>
									<Grid.Row>
										<Grid.Column style={{ textAlign: "center" }}>
											<div style={{ margin: "10px" }}>
												<Header as="h3" attached="top">
													<div>
														<span style={{ color: "teal", fontSize: "0.9rem" }}>Patient sélectionné</span>
													</div>
												</Header>

												<Segment attached style={{ margn: "10px" }}>
													<PatientData patient={{ ...patient, email: values.email }} />
													{!editEmail && !emailValid && (
														<>
															L'adresse email actuelle du patient est&nbsp;: <b>{patient?.email ? patient?.email : "n.c."}</b>
															<br />
															Cette adresse email est-elle correcte&nbsp;? <br />
															<Button basic color="green" onClick={() => setEmailValid(true)}>
																Oui
															</Button>
															<Button basic color="red" onClick={() => setEditEmail(true)}>
																Non
															</Button>
														</>
													)}
													{editEmail && !emailValid && (
														<>
															<Field component={CustomInputTextComponent} name="email" customlabel="Adresse email corrigée" type="text" required />
															<BlueButtonEditBloc
																icon="check"
																text="Valider l'adresse email"
																onClick={() => {
																	if (values.email && values.email.length > 5) {
																		setEmailValid(true);
																		setEditEmail(false);
																	} else {
																		toast.error("L'adresse email semble incorrecte");
																	}
																}}
															/>
														</>
													)}
												</Segment>
											</div>
										</Grid.Column>
										<Grid.Column>
											<input type="file" ref={fileUploadRef} style={{ opacity: "0", height: 0, width: 0 }} onChange={onFileChange} />
											<div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "center" }}>
												{files && files.length > 0 && (
													<div>
														{files.map((file) => (
															<Segment
																padded
																key={file.name}
																style={{
																	display: "flex",
																	justifyContent: "space-between",
																	fontSize: "0.8rem",
																	padding: "1rem",
																	marginBottom: "0.5rem",
																	border: "1px solid #ddd",
																	borderRadius: "5px",
																	background: "#eef9f9",
																}}
															>
																{file.name}
																<Label icon circular floating as="a" onClick={() => handleRemoveFile(file.name)} style={{ width: "15px" }}>
																	<Icon name="x" color="black" style={{ margin: "0 2px" }} />
																</Label>
															</Segment>
														))}
													</div>
												)}
												{files?.length === 0 && (
													<div
														style={{
															display: "flex",
															justifyContent: "center",
															alignItems: "center",
															flexGrow: 4,
															flexShrink: 0,
															height: "100%",
															fontStyle: "italic",
															color: "#999",
															marginBottom: "1rem",
														}}
													>
														<i>Aucun fichier </i>
													</div>
												)}
												<div
													style={{
														display: "flex",
														justifyContent: "center",
														margin: "5px 0",
													}}
												>
													<BlueButtonEditBloc
														text="Ajouter une ordonnance"
														icon="search plus"
														onClick={(e) => {
															if (e !== undefined) e.preventDefault();
															fileUploadRef.current.click();
														}}
													/>
												</div>
											</div>
										</Grid.Column>
									</Grid.Row>
								</Grid>
							</Modal.Content>

							<Modal.Actions>
								<Button
									onClick={(e) => {
										if (e !== undefined) e.preventDefault();
										closeModal();
									}}
								>
									<Icon name="remove" /> Annuler
								</Button>
								<Button
									primary
									onClick={(e) => {
										if (e !== undefined) e.preventDefault();
										console.log("click submit form", values, files);
										submitFormToCheckin(values);
									}}
									disabled={files.length === 0 || !emailValid}
								>
									<Icon name="mail outline" /> Créer
								</Button>
							</Modal.Actions>
						</Modal>
					</Form>
				</>
			)}
		</Formik>
	);
};

export default CheckinModal;
